/**
 * Small formatting rules shared by the two edge report sections and the node
 * page's uploads card.
 *
 * They live apart from the upload page's own copies on purpose: the edge
 * sections mount on two different report tabs and on the node page, and each of
 * those must label a Baghdad day the same way the server counted it.
 */

import { UNKNOWN } from '../../lib/format';
import type { UploadBucket } from '../../types/api';

/**
 * A bucket's first day, labelled for its size.
 *
 * Buckets are calendar days (`YYYY-MM-DD`). They are parsed at UTC midnight
 * and formatted in UTC, so the browser's own zone can never move one to the
 * day before — the classic off-by-one of `new Date('2026-09-28')` rendered in
 * a zone west of Greenwich.
 */
export function bucketLabeller(bucket: UploadBucket): (at: string) => string {
  const label = (day: string, options: Intl.DateTimeFormatOptions) => {
    const at = Date.parse(`${day}T00:00:00Z`);
    return Number.isFinite(at)
      ? new Date(at).toLocaleDateString(undefined, { ...options, timeZone: 'UTC' })
      : day;
  };
  switch (bucket) {
    case 'day':
      return (at) => label(at, { month: 'short', day: 'numeric' });
    case 'week':
      return (at) => `Wk ${label(at, { month: 'short', day: 'numeric' })}`;
    case 'month':
      return (at) => label(at, { month: 'short', year: 'numeric' });
  }
}

/**
 * A LineChart x value for a Baghdad day.
 *
 * LineChart takes instants and labels them in the browser's zone. Midday UTC
 * is the same calendar date in every zone from UTC−11 to UTC+11, so a day
 * plotted there is labelled as itself wherever the reader is.
 */
export function dayAsInstant(day: string): string {
  return `${day}T12:00:00Z`;
}

/** A 0–1 share as a percentage — or the unknown marker, never "0%". */
export function percent(value: number | null | undefined, digits = 1): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? UNKNOWN
    : `${(value * 100).toFixed(digits)}%`;
}

export function rangeText(from: string, to: string): string {
  return from === to ? `${from} (Baghdad day)` : `${from} to ${to} (Baghdad days)`;
}

/** Query string for an edge report: Baghdad days plus the page's scope. */
export function edgeQuery(
  params: Record<string, string>,
  scope: { slug?: string | null; node?: string | null },
): string {
  const qs = new URLSearchParams(params);
  // The server refuses both at once (400); a node is the narrower of the two,
  // so it wins rather than the request failing.
  if (scope.node) qs.set('node', scope.node);
  else if (scope.slug) qs.set('slug', scope.slug);
  return qs.toString();
}
